import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi, type Me } from '@/api';

/**
 * Auth-Store. Haelt den eingeloggten User im Speicher.
 *
 * `restore()` ist promise-cached: wenn mehrere Stellen (App.vue,
 * Router-Guard) gleichzeitig restore() aufrufen, warten alle auf DENSELBEN
 * Fetch. Vorher gab's eine Race-Condition wo der zweite Caller sofort
 * returned hat und der Router-Guard daraufhin „nicht eingeloggt" angenommen
 * hat → User wurde bei Ctrl+R rausgeworfen.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<Me | null>(null);
  const attempted = ref(false);
  let pending: Promise<void> | null = null;

  const isAdmin = computed(() => !!user.value && user.value.role === 'admin');

  async function restore(): Promise<void> {
    if (pending) return pending;
    if (attempted.value) return;

    pending = (async () => {
      try {
        user.value = await authApi.me();
      } catch {
        user.value = null;
      } finally {
        attempted.value = true;
        pending = null;
      }
    })();
    return pending;
  }

  async function redeem(code: string): Promise<Me> {
    const me = await authApi.redeem(code);
    user.value = me;
    attempted.value = true;
    return me;
  }

  async function logout() {
    try { await authApi.logout(); }
    finally {
      user.value = null;
      attempted.value = true;
    }
  }

  return { user, attempted, isAdmin, restore, redeem, logout };
});
